/**
 * @file master.ts
 * @module api/apiJSON/master
 * @description API JSON payload builders for master data endpoints.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.1.1
 */

import { reference } from "@popperjs/core";
import {
  IAddBusinessCertificates,
  IListBusinessCertificates,
  IAddPrimaryCategory,
  IAddSubCategory,
  IAddGroupCategory,
  IListCategory,
  IAddBusinessTypes,
  IListBusinessTypes,
  IEditBusinessTypes,
  IAddBrands,
  IEditBrands,
  IListBrands,
  IAddTechnicalDetails,
  IAddVariants,
  IVariantInfo,
  IAddVehicle,
  IListVehicle,
  IUpdateVehicleState,
  IUpdateVehicleDriver,
} from "../../types";
import {
  IAddArea,
  IAssignGoodsLoadingArea,
  IEditLoadingArea,
  IListArea,
  IRolesAndPermissions,
  IUpdateAreaState,
  IUserManage,
  OrderItem,
} from "../../types/request_data/master";
import { MultiSelection, SingleSelection } from "../../utils/constants";
export const businessJSON = {
  addBusinessCertificate: ({
    businessCertificates,
  }: IAddBusinessCertificates) => {
    let form = new FormData();
    businessCertificates.map((val: any, index: any) => {
      form.append("list[" + [index] + "][title]", val.certificateName);
      form.append("list[" + [index] + "][image]", val.certificateImageReader);
      form.append("list[" + [index] + "][expiryRequire]", val.expiry);
      if (val.category.length) {
        val.category.map((categoryVal: any, categoryIndex: number) => {
          return form.append(
            "list[" + [index] + "][categories][" + categoryIndex + "]",
            categoryVal
          );
        });
      }
    });
    return form;
  },
  editBusinessCertificate: ({
    businessCertificates,
  }: IAddBusinessCertificates) => {
    let form = new FormData();
    if (businessCertificates.certificateImageReader)
      form.append("image", businessCertificates.certificateImageReader);
    form.append("title", businessCertificates.certificateName);
    if (businessCertificates.expiry !== undefined)
      form.append("expiryRequire", businessCertificates.expiry);
    businessCertificates.category.map(
      (categoryVal: any, categoryIndex: number) => {
        return form.append("categories[" + categoryIndex + "]", categoryVal);
      }
    );
    return form;
  },
  listBusinessCertificates: ({
    pageNo,
    limit,
    sortKey,
    sortOrder,
    warehouse,
  }: IListBusinessCertificates) => {
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
    };
    if (warehouse) {
      params = {
        ...params,
        warehouse: warehouse,
      };
    }
    return params;
  },
};
export const categoryJSON = {
  addPrimaryCategory: ({ primaryCategory }: IAddPrimaryCategory) => {
    let form = new FormData();
    primaryCategory.map((val: any, index: any) => {
      form.append("list[" + [index] + "][title]", val.categoryName);
      form.append("list[" + [index] + "][image]", val.categoryImageReader);
      form.append("list[" + [index] + "][isTobacco]", val.isTobacco);
      form.append(
        "list[" + [index] + "][isPharmacyCategory]",
        val.isPharmacyCategory
      );
    });

    return form;
  },
  editPrimaryCategory: ({ primaryCategory }: IAddPrimaryCategory) => {
    let form = new FormData();
    form.append("title", primaryCategory.categoryName);
    form.append("isTobacco", primaryCategory.isTobacco);
    form.append("isPharmacyCategory", primaryCategory.isPharmacyCategory);
    if (primaryCategory.categoryImageReader) {
      form.append("image", primaryCategory.categoryImageReader);
    }
    if (primaryCategory.activeCategoryImageReader instanceof File) {
      form.append("activeImage", primaryCategory.activeCategoryImageReader);
    }
    if (primaryCategory.svgCategoryImageReader instanceof File) {
      form.append("imageSvg", primaryCategory.svgCategoryImageReader);
    }
    return form;
  },
  addSubCategory: ({ subCategory }: IAddSubCategory) => {
    let form = new FormData();
    subCategory.map((val: any, index: any) => {
      form.append("list[" + [index] + "][title]", val.subCategoryName);
      form.append("list[" + [index] + "][image]", val.subCategoryImageReader);
      form.append("list[" + [index] + "][category]", val.primaryCategory);
    });
    return form;
  },
  editSubCategory: ({ subCategory }: IAddSubCategory) => {
    let form = new FormData();
    form.append("title", subCategory.subCategoryName);
    form.append("image", subCategory.subCategoryImageReader);
    form.append("category", subCategory.primaryCategory);
    return form;
  },
  addGroupCategory: ({ groupCategory }: IAddGroupCategory) => {
    let form = new FormData();
    groupCategory.map((val: any, index: any) => {
      form.append("list[" + [index] + "][title]", val.groupCategoryName);
      form.append("list[" + [index] + "][category]", val.primaryCategory);
      form.append("list[" + [index] + "][subCategory]", val.subCategory);
    });
    return form;
  },
  editGroupCategory: ({ groupCategory }: IAddGroupCategory) => {
    let form = new FormData();
    form.append("title", groupCategory.groupCategoryName);
    form.append("category", groupCategory.primaryCategory);
    form.append("subCategory", groupCategory.subCategory);
    return form;
  },
  listCategory: ({
    pageNo,
    limit,
    sortKey,
    sortOrder,
    categoriesDepth,
    warehouse,
  }: IListCategory) => {
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      categoriesDepth: categoriesDepth,
    };
    if (warehouse) {
      params = {
        ...params,
        warehouse: warehouse,
      };
    }
    return params;
  },
};
export const businessTypeJSON = {
  addBusinessTypes: ({ businessTypes }: IAddBusinessTypes) => {
    let form = new FormData();
    businessTypes.map((val: any, index: any) => {
      form.append("list[" + [index] + "][title]", val.businessName);
      form.append("list[" + [index] + "][image]", val.businessImageReader);
      form.append("list[" + [index] + "][abbreviation]", val.abbreviation);
      if (val.addedFor.length) {
        val.addedFor.map((val: any, addIndex: number) => {
          return form.append(
            "list[" + [index] + "][addedFor][" + addIndex + "]",
            val
          );
        });
      }
    });
    return form;
  },
  listBusinessTypes: ({
    pageNo,
    limit,
    sortKey,
    sortOrder,
    warehouse,
  }: IListBusinessTypes) => {
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
    };
    if (warehouse) {
      params = {
        ...params,
        warehouse: warehouse,
      };
    }
    return params;
  },
  editBusinessTypes: ({
    title,
    addedFor,
    image,
    abbreviation,
  }: IEditBusinessTypes) => {
    let form = new FormData();
    if (image) form.append("image", image);
    form.append("title", title);
    if (addedFor.length) {
      addedFor.map((addVal: any, index: number) => {
        form.append("addedFor[" + index + "]", addVal);
      });
    }
    form.append("abbreviation", abbreviation ? abbreviation : "");
    // for (const pair of form.entries()) {
    //   console.log(`${pair[0]}, ${pair[1]}`)
    // }
    return form;
  },
};
export const brandsJSON = {
  addBrands: ({ brands }: IAddBrands) => {
    let form = new FormData();
    brands.map((val: any, index: any) => {
      form.append("list[" + [index] + "][title]", val.brandName);
      form.append("list[" + [index] + "][image]", val.brandImageReader);
    });
    return form;
  },
  editBrands: ({ title, image }: IEditBrands) => {
    let form = new FormData();
    if (image) form.append("image", image);
    form.append("title", title);
    return form;
  },
  listBrands: ({
    pageNo,
    limit,
    sortKey,
    sortOrder,
    searchTerm,
    warehouse,
  }: IListBrands) => {
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      searchTerm: searchTerm,
    };
    if (warehouse) {
      params = {
        ...params,
        warehouse: warehouse,
      };
    }
    return params;
  },
};

/**
 * Object providing helper methods to format and filter Technical Details for API payloads.
 */
export const technicalDetailsJSON = {
  /**
   * Processes and filters technical details for addition.
   *
   * Selection fields (Single/Multi) are included only if they have options.
   * All included fields must have a non-empty name.
   * Empty categories (categories with no valid fields) are excluded from the result.
   *
   * @param {IAddTechnicalDetails} params - The technical details and optional audit log message.
   * @returns {Array} A filtered list of categories and fields for the API payload.
   */
  addTechnicalDetails: ({
    technicalDetails,
    auditLogMessage,
  }: IAddTechnicalDetails) => {
    let data: any = [];
    technicalDetails.map((techVal: any) => {
      let temp: any = [];
      techVal.fields.map((val: any) => {
        if (val.type === SingleSelection || val.type === MultiSelection) {
          if (val.name !== "" && val.options.length > 0) {
            temp.push(val);
          }
        } else {
          if (val.name !== "") {
            temp.push(val);
          }
        }
      });
      if (temp.length) {
        data.push({
          category: techVal.category,
          fields: temp,
          ...(auditLogMessage && { auditLogMessage }),
        });
      }
    });
    return data;
  },
  /**
   * Prepares technical details for an edit operation.
   *
   * @param {IAddTechnicalDetails} params - Object containing the technical details to edit.
   * @returns {Array} The technical details as provided.
   */
  editTechnicalDetails: ({ technicalDetails }: IAddTechnicalDetails) => {
    return technicalDetails;
  },
};

export const variantsJSON = {
  addVariants: ({ variants }: IAddVariants) => {
    return variants;
  },
  editTechnicalDetails: ({ technicalDetails }: IAddTechnicalDetails) => {
    return technicalDetails;
  },
  variantInfo: ({ viewType }: IVariantInfo) => {
    return {
      viewType: viewType,
    };
  },
};
export const vehicleJSON = {
  addVehicle: ({
    name,
    number,
    imageReader,
    user,
    width,
    weight,
    length,
    height,
    hours,
    travelTime,
    warehouse,
    districts,
    ward,
    includeInAutoRoutePlanning,
  }: IAddVehicle) => {
    let form = new FormData();
    form.append("name", name);
    form.append("number", number);
    form.append("image", imageReader);
    if (user) {
      form.append("user", user);
    }
    form.append("dimensions[width]", width);
    form.append("dimensions[height]", height);
    form.append("dimensions[length]", length);
    form.append("dimensions[weight]", weight);
    districts.forEach((item: any, index: number) => {
      form.append(`district[${index}][reference]`, item.reference);
      form.append(`district[${index}][name]`, item.name);
    });
    if (ward && ward.length > 0) {
      ward.forEach((item: any, index: number) => {
        form.append(`ward[${index}][reference]`, item.reference);
        form.append(`ward[${index}][name]`, item.name);
      });
    }
    form.append("hours", hours.toString());
    if (travelTime) form.append("travelTime", travelTime.toString());
    form.append(
      "includeInAutoRoutePlanning",
      String(includeInAutoRoutePlanning !== false)
    );
    if (warehouse) {
      form.append("warehouse", warehouse.value);
    }
    return form;
  },
  listVehicle: ({
    limit,
    sortKey,
    sortOrder,
    pageNo,
    searchTerm,
    warehouse,
  }: IListVehicle) => {
    return {
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      pageNo: pageNo,
      searchTerm: searchTerm,
      warehouse: warehouse,
    };
  },
  updateStatus: ({ active }: IUpdateVehicleState) => {
    return { active: active };
  },
  updateDriver: ({ user }: IUpdateVehicleDriver) => {
    let form = new FormData();
    form.append("user", user);
    return form;
  },
};
export const loadingAreaJSON = {
  addArea: ({ areas }: IAddArea) => {
    let form = new FormData();
    areas.map((val: any, index: any) => {
      form.append("list[" + [index] + "][name]", val.name);
      form.append("list[" + [index] + "][image]", val.imageReader);
      // form.append('warehouse',val.warehouse.value)
    });
    return form;
  },
  listArea: ({
    limit,
    sortKey,
    sortOrder,
    pageNo,
    searchTerm,
    warehouse,
  }: IListArea) => {
    return {
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      pageNo: pageNo,
      searchTerm: searchTerm,
      warehouse: warehouse,
    };
  },
  updateStatus: ({ active }: IUpdateAreaState) => {
    return { active: active };
  },
  editLoadingArea: ({ name, image, warehouse }: IEditLoadingArea) => {
    let form = new FormData();
    if (image) form.append("image", image);
    form.append("name", name);
    if (warehouse) {
      form.append("warehouse", warehouse);
    }
    return form;
  },
  assignLoadingArea: ({ assignLoadingArea }: IAssignGoodsLoadingArea) => {
    let list: any = [];
    if (assignLoadingArea) {
      assignLoadingArea.map((val: any) => {
        let temp: any = { user: val._id, goodsLoadingAreas: [] };
        if (val.goodsLoadingAreas && val.goodsLoadingAreas.length) {
          val.goodsLoadingAreas.map((areas: any) => {
            temp.goodsLoadingAreas.push(areas.reference);
          });
        }
        list.push(temp);
      });
    }
    return { list: list };
  },
};
export const rolesJSON = {
  addRoles: ({
    name,
    permission,
    isWarehouseManagerRole,
    warehouse,
  }: IRolesAndPermissions) => {
    // const tempPermission = permission.map((item: any) => {
    //   const temp = { ...item };
    //   if (temp.permissions.length >= 4) {
    //     temp.permissions = [0];
    //   }
    //   return temp;
    // });
    return {
      name: name,
      permission: permission,
      isWarehouseManagerRole: isWarehouseManagerRole,
      warehouse: warehouse?.reference || "",
    };
  },
};
export const userManagementJson = {
  addUser: ({ name, phone, phoneCountry, email, role, image }: IUserManage) => {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("phone", phone);
    formData.append("phoneCountry", phoneCountry);
    formData.append("email", email);
    formData.append("role", role);
    formData.append("image", image);
    // formData.append('password', '12345678');
    return formData;
  },
  editUser: ({
    name,
    phone,
    phoneCountry,
    email,
    role,
    image,
    prevRole,
  }: IUserManage) => {
    return {
      name: name,
      phone: phone,
      phoneCountry: phoneCountry,
      email: email,
      role: role,
      image: image,
      isRoleUpdated: !(prevRole === role),
    };
  },
};
export const deliveryMessageJson = {
  message: ({ order }: { order: OrderItem[] }) => {
    return {
      orderList: order.map((item: any) => ({
        buyer: item.buyer,
        refKeys: item.refKeys,
      })),
    };
  },
};
